import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IComponentWithChildren } from 'types/app';
import {
    IChatbotContext,
    IChatbotState,
    IConversation,
    IMessage,
} from 'types/chatbot';
import useAPIChatbot from 'hooks/api/useAPIChatbot';
import { useConversationStore } from 'helpers/conversationStore';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import { extractErrorMessage, isCreditsExhaustedError, truncateReason } from 'helpers/chatbotErrors';

const ChatbotContext = createContext<IChatbotContext | undefined>(undefined);

export const useChatbot = (): IChatbotContext => {
    const context = useContext(ChatbotContext);
    if (!context) {
        throw new Error('useChatbot must be used within a ChatbotProvider');
    }
    return context;
};

const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

// A local conversation that never reached Dify (nothing sent yet) is a
// placeholder: select it again instead of stacking "Nouvelle conversation"
// entries every time the current conversation is archived or deleted.
const isEmptyLocalConversation = (conv: IConversation): boolean =>
    !conv.externalId && conv.messages.length === 0 && !conv.isArchived;

const selectOrCreateEmptyConversation = (
    conversations: IConversation[],
    title: string,
): Pick<IChatbotState, 'conversations' | 'currentConversationId'> => {
    const existing = conversations.find(isEmptyLocalConversation);
    if (existing) {
        return { conversations, currentConversationId: existing.id };
    }
    const newConversation: IConversation = {
        id: generateId(),
        title,
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date(),
    };
    return { conversations: [newConversation, ...conversations], currentConversationId: newConversation.id };
};

// The backend lists threads without dates: once the history is loaded, date
// the conversation from its first and last message.
const withHistoryDates = (
    conv: IConversation,
    messages: IMessage[],
): Pick<IConversation, 'createdAt' | 'updatedAt'> => {
    if (!messages.length) return { createdAt: conv.createdAt, updatedAt: conv.updatedAt };
    const times = messages.map((msg) => msg.timestamp.getTime());
    return {
        createdAt: conv.createdAt || new Date(Math.min(...times)),
        updatedAt: new Date(Math.max(...times)),
    };
};

export const ChatbotProvider: React.FC<IComponentWithChildren> = ({ children }) => {
    const { t } = useTranslation();
    const { user } = useContext(UserContext) as UserContextProps;

    const { 
        fetchThreads, 
        sendMessage: sendAPIMessage,
        fetchMessages,
        archiveThread,
        unarchiveThread,
        deleteThread,
        renameThread,
        generateTitle,
        stopStreaming: stopStreamingAPI,
    } = useAPIChatbot();
    const [state, setState] = useState<IChatbotState>({
        isOpen: false,
        isSidebarOpen: false,
        conversations: [],
        currentConversationId: null,
        isLoadingThreads: false,
        unavailableReason: null,
    });
    
    // Track active stream abort controller
    const streamAbortControllerRef = useRef<AbortController | null>(null);
    // Track current task ID for stopping streaming
    const currentTaskIdRef = useRef<string | null>(null);

    // Reason shown in the "coach unavailable" message: OpenRouter credits
    // exhausted gets its own wording, any other backend / Dify error is quoted.
    const formatUnavailableReason = useCallback(
        (rawReason?: string | null): string => {
            if (isCreditsExhaustedError(rawReason)) return t('chatbot.creditsexhausted');
            if (!rawReason) return t('chatbot.unreachable');
            return t('chatbot.quotedreason', { message: truncateReason(rawReason) });
        },
        [t],
    );

    useEffect(() => {
        // Only load threads if the chatbot switch is enabled for this user
        if (!user?.profileInfo?.chatbotEnabled) {
            return;
        }

        const loadThreads = async () => {
            setState((prev) => ({ ...prev, isLoadingThreads: true }));
            const { threads, failed, error } = await fetchThreads();
            if (threads && !failed) {
                const conversations: IConversation[] = threads.map((thread) => ({
                    id: thread.id,
                    title: thread.title,
                    messages: [],
                    createdAt: thread.createdAt,
                    updatedAt: thread.updatedAt,
                    externalId: thread.id,
                }));
                setState((prev) => ({
                    ...prev,
                    conversations,
                    isLoadingThreads: false,
                    unavailableReason: null,
                }));
            } else {
                // Backend / Dify unreachable: tell the user why the coach is unavailable
                setState((prev) => ({
                    ...prev,
                    isLoadingThreads: false,
                    unavailableReason: formatUnavailableReason(error),
                }));
            }
        };

        loadThreads();
    }, [user?.profileInfo?.chatbotEnabled]);

    const openChatbot = useCallback(async () => {
        setState((prev) => {
            // Check if there's a current conversation
            const currentConv = prev.conversations.find(c => c.id === prev.currentConversationId);
            
            // If we have a current conversation, just reopen the chatbot
            if (currentConv) {
                return {
                    ...prev,
                    isOpen: true,
                };
            }
            
            // No current conversation - select the most recent non-archived one if available
            const mostRecentConv = prev.conversations.find(c => !c.isArchived);
            if (mostRecentConv) {
                // Set the conversation ID in store if it has an externalId
                if (mostRecentConv.externalId) {
                    useConversationStore.setConversationId(mostRecentConv.externalId);
                    
                    // Load messages for this conversation
                    (async () => {
                        setState((prev) => ({
                            ...prev,
                            conversations: prev.conversations.map(conv =>
                                conv.id === mostRecentConv.id ? { ...conv, isLoadingHistory: true } : conv
                            ),
                        }));

                        const messages = await fetchMessages(mostRecentConv.externalId!);
                        
                        if (messages) {
                            const formattedMessages: IMessage[] = messages.map((msg) => ({
                                id: msg.id,
                                text: msg.content.find(c => c.type === 'text')?.text || '',
                                sender: msg.role === 'user' ? 'user' : 'bot',
                                timestamp: new Date(msg.createdAt),
                            }));

                            setState((prev) => ({
                                ...prev,
                                conversations: prev.conversations.map(conv =>
                                    conv.id === mostRecentConv.id 
                                        ? {
                                            ...conv,
                                            ...withHistoryDates(conv, formattedMessages),
                                            messages: formattedMessages,
                                            isLoadingHistory: false,
                                        }
                                        : conv
                                ),
                            }));
                        } else {
                            setState((prev) => ({
                                ...prev,
                                conversations: prev.conversations.map(conv =>
                                    conv.id === mostRecentConv.id ? { ...conv, isLoadingHistory: false } : conv
                                ),
                            }));
                        }
                    })();
                }
                return {
                    ...prev,
                    isOpen: true,
                    currentConversationId: mostRecentConv.id,
                };
            }
            
            // No conversations at all - create a new one
            const newConversation: IConversation = {
                id: generateId(),
                title: t('chatbot.newconversation'),
                messages: [],
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            
            // Clear conversation store for new conversation
            useConversationStore.clearConversationId();
            
            return {
                ...prev,
                isOpen: true,
                conversations: [newConversation, ...prev.conversations],
                currentConversationId: newConversation.id,
            };
        });
    }, [fetchMessages]);

    const closeChatbot = useCallback(() => {
        setState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const toggleSidebar = useCallback(() => {
        setState((prev) => ({ ...prev, isSidebarOpen: !prev.isSidebarOpen }));
    }, []);

    const createConversation = useCallback(async () => {
        setState((prev) => {
            // Clear conversation ID for new thread
            useConversationStore.clearConversationId();

            return {
                ...prev,
                ...selectOrCreateEmptyConversation(prev.conversations, t('chatbot.newconversation')),
                isSidebarOpen: false,
            };
        });

        // Note: We don't call createThread here anymore since Dify creates
        // the conversation automatically on the first message
    }, []);

    const selectConversation = useCallback(async (id: string) => {
        // Abort any active streaming when switching conversations
        if (streamAbortControllerRef.current) {
            streamAbortControllerRef.current.abort();
            streamAbortControllerRef.current = null;
        }

        setState((prev) => ({
            ...prev,
            currentConversationId: id,
            isSidebarOpen: false,
        }));

        // Get the conversation to check if it has a Dify conversation ID
        const conversation = state.conversations.find(conv => conv.id === id);
        
        // Set the conversation ID in the store if this conversation has one
        if (conversation?.externalId) {
            useConversationStore.setConversationId(conversation.externalId);
            
            // Always fetch fresh messages from server to ensure we have complete messages
            setState((prev) => ({
                ...prev,
                conversations: prev.conversations.map(conv =>
                    conv.id === id ? { ...conv, isLoadingHistory: true } : conv
                ),
            }));

            const messages = await fetchMessages(conversation.externalId);
            
            if (messages) {
                const formattedMessages: IMessage[] = messages.map((msg) => ({
                    id: msg.id,
                    text: msg.content.find(c => c.type === 'text')?.text || '',
                    sender: msg.role === 'user' ? 'user' : 'bot',
                    timestamp: new Date(msg.createdAt),
                }));

                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map(conv =>
                        conv.id === id 
                            ? {
                                ...conv,
                                ...withHistoryDates(conv, formattedMessages),
                                messages: formattedMessages,
                                isLoadingHistory: false,
                            }
                            : conv
                    ),
                }));
            } else {
                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map(conv =>
                        conv.id === id ? { ...conv, isLoadingHistory: false } : conv
                    ),
                }));
            }
        } else {
            // Clear if it's a new conversation
            useConversationStore.clearConversationId();
        }
    }, [fetchMessages, state.conversations]);

    const deleteConversation = useCallback(async (id: string) => {
        const conversation = state.conversations.find(conv => conv.id === id);
        
        // If it has an externalId, delete from server
        if (conversation?.externalId) {
            const success = await deleteThread(conversation.externalId);
            if (!success) {
                return; // Don't delete locally if server delete failed
            }
        }

        setState((prev) => {
            const updatedConversations = prev.conversations.filter((conv) => conv.id !== id);
            const isCurrentDeleted = prev.currentConversationId === id;

            // If we deleted the current conversation, move to an empty one
            if (isCurrentDeleted) {
                // Clear conversation store for new conversation
                useConversationStore.clearConversationId();

                return {
                    ...prev,
                    ...selectOrCreateEmptyConversation(updatedConversations, t('chatbot.newconversation')),
                };
            }

            return {
                ...prev,
                conversations: updatedConversations,
            };
        });
    }, [deleteThread, state.conversations]);

    const archiveConversation = useCallback(async (id: string) => {
        const conversation = state.conversations.find(conv => conv.id === id);
        
        if (conversation?.externalId) {
            const success = await archiveThread(conversation.externalId);
            if (success) {
                setState((prev) => {
                    const isCurrentArchived = prev.currentConversationId === id;
                    const archivedConversations = prev.conversations.map(conv =>
                        conv.id === id ? { ...conv, isArchived: true } : conv
                    );

                    // If we archived the current conversation, move to an empty one
                    if (isCurrentArchived) {
                        // Clear conversation store for new conversation
                        useConversationStore.clearConversationId();

                        return {
                            ...prev,
                            ...selectOrCreateEmptyConversation(archivedConversations, t('chatbot.newconversation')),
                            isSidebarOpen: false,
                        };
                    }

                    return {
                        ...prev,
                        conversations: archivedConversations,
                        isSidebarOpen: false,
                    };
});
            }
        }
    }, [archiveThread, state.conversations]);

    const unarchiveConversation = useCallback(async (id: string) => {
        const conversation = state.conversations.find(conv => conv.id === id);
        
        if (conversation?.externalId) {
            const success = await unarchiveThread(conversation.externalId);
            if (success) {
                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map(conv =>
                        conv.id === id ? { ...conv, isArchived: false } : conv
                    ),
                }));
            }
        }
    }, [unarchiveThread, state.conversations]);

    const renameConversation = useCallback(async (id: string, title: string) => {
        const conversation = state.conversations.find(conv => conv.id === id);
        
        // Update locally first for immediate feedback
        setState((prev) => ({
            ...prev,
            conversations: prev.conversations.map(conv =>
                conv.id === id ? { ...conv, title } : conv
            ),
        }));

        if (conversation?.externalId) {
            const success = await renameThread(conversation.externalId, title);
            if (!success) {
                // Revert if failed
                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map(conv =>
                        conv.id === id ? { ...conv, title: conversation.title } : conv
                    ),
                }));
            }
        }
    }, [renameThread, state.conversations]);

    const sendMessage = useCallback(async (text: string) => {
        if (!text.trim()) return;

        const userMessage: IMessage = {
            id: generateId(),
            text: text.trim(),
            sender: 'user',
            timestamp: new Date(),
        };

        let currentConversation: IConversation | undefined;
        let targetConversationId: string | null = null;

        setState((prev) => {
            const conversationId = prev.currentConversationId;
            if (!conversationId) return prev;

            targetConversationId = conversationId; // Capture the target conversation ID

            const updatedConversations = prev.conversations.map((conv) => {
                if (conv.id === conversationId) {
                    currentConversation = conv;
                    const updatedMessages = [...conv.messages, userMessage];
                    
                    // Auto-generate title on first message if needed
                    const needsTitle = conv.messages.length === 0;
                    const title = needsTitle
                        ? text.trim().substring(0, 30) + (text.length > 30 ? '...' : '')
                        : conv.title;

                    return {
                        ...conv,
                        messages: updatedMessages,
                        title,
                        updatedAt: new Date(),
                    };
                }
                return conv;
            });

            return {
                ...prev,
                conversations: updatedConversations,
            };
        });

        if (!targetConversationId) return; // Safety check

        const botMessageId = generateId();
        const initialBotMessage: IMessage = {
            id: botMessageId,
            text: '',
            sender: 'bot',
            timestamp: new Date(),
            isStreaming: true,
        };

        setState((prev) => {
            const updatedConversations = prev.conversations.map((conv) => {
                if (conv.id === targetConversationId) {
                    return {
                        ...conv,
                        messages: [...conv.messages, initialBotMessage],
                        updatedAt: new Date(),
                    };
                }
                return conv;
            });

            return {
                ...prev,
                conversations: updatedConversations,
            };
        });

        // Get conversation ID from store (only exists if we've already chatted with Dify)
        const storedConversationId = useConversationStore.getState().conversationId;

        const messages = [
            {
                role: 'user',
                content: [{ type: 'text', text: text.trim() }],
            },
        ];

        // Create abort controller for this stream
        const abortController = new AbortController();
        streamAbortControllerRef.current = abortController;

        // Only send remoteId if we have a real Dify conversation ID from the store
        // Don't send the localId for new threads - let Dify create the conversation
        const sendResult = await sendAPIMessage(messages, storedConversationId || undefined, abortController.signal);

        const updateBotMessage = (
            update: (msg: IMessage) => IMessage,
            extra: Partial<IChatbotState> = {},
        ) => {
            setState((prev) => ({
                ...prev,
                ...extra,
                conversations: prev.conversations.map((conv) => {
                    if (conv.id !== targetConversationId) return conv;
                    return {
                        ...conv,
                        messages: conv.messages.map((msg) => (msg.id === botMessageId ? update(msg) : msg)),
                    };
                }),
            }));
        };

        const removeBotMessage = () => {
            setState((prev) => ({
                ...prev,
                conversations: prev.conversations.map((conv) =>
                    conv.id === targetConversationId
                        ? { ...conv, messages: conv.messages.filter((msg) => msg.id !== botMessageId) }
                        : conv,
                ),
            }));
        };

        // Replace the streaming placeholder with the "coach unavailable" notice
        const failBotMessage = (rawReason?: string | null) => {
            updateBotMessage((msg) => ({
                ...msg,
                text: t('chatbot.unavailable', { reason: formatUnavailableReason(rawReason) }),
                isStreaming: false,
                isError: true,
            }));
        };

        if (!sendResult.stream) {
            if (streamAbortControllerRef.current === abortController) {
                streamAbortControllerRef.current = null;
            }
            if (sendResult.aborted) {
                removeBotMessage();
            } else {
                // Backend unreachable or answered with an error (Dify down, provider failure...)
                failBotMessage(sendResult.error);
            }
            return;
        }

        const reader = sendResult.stream.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let difyConversationId = '';
        let lastUpdateTime = 0;
        const UPDATE_THROTTLE = 0; // Update UI every 50ms for smoother rendering
        let buffer = ''; // Buffer for incomplete SSE lines
        // Dify reports provider failures (e.g. OpenRouter credits exhausted) as an SSE "error" event
        let streamError: string | null = null;
        let receivedMessageEnd = false;

        // Final update with complete text
        const finalizeBotMessage = () => {
            updateBotMessage(
                (msg) => ({ ...msg, text: fullResponse, isStreaming: false }),
                { unavailableReason: null },
            );
        };

        try {
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                const lines = buffer.split('\n');

                // Keep the last incomplete line in the buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6).trim();
                        if (data === '[DONE]') continue;
                        if (!data) continue; // Skip empty data

                        try {
                            const parsed = JSON.parse(data);

                            if (parsed.event === 'message' || parsed.event === 'agent_message') {
                                // Store the Dify conversation ID
                                if (parsed.conversation_id) {
                                    difyConversationId = parsed.conversation_id;
                                    useConversationStore.setConversationId(parsed.conversation_id);
                                }

                                // Store the task ID for stopping streaming
                                if (parsed.task_id) {
                                    currentTaskIdRef.current = parsed.task_id;
                                }

                                if (parsed.answer) {
                                    fullResponse += parsed.answer;

                                    // Throttle UI updates to reduce re-renders
                                    const now = Date.now();
                                    if (now - lastUpdateTime >= UPDATE_THROTTLE) {
                                        lastUpdateTime = now;

                                        setState((prev) => {
                                            return {
                                                ...prev,
                                                conversations: prev.conversations.map((conv) => {
                                                    if (conv.id === targetConversationId) {
                                                        const updatedMessages = conv.messages.map((msg) => {
                                                            if (msg.id === botMessageId) {
                                                                return {
                                                                    ...msg,
                                                                    text: fullResponse,
                                                                };
                                                            }
                                                            return msg;
                                                        });

                                                        return {
                                                            ...conv,
                                                            messages: updatedMessages,
                                                            externalId: difyConversationId || conv.externalId,
                                                            updatedAt: new Date(),
                                                        };
                                                    }
                                                    return conv;
                                                }),
                                            };
                                        });
                                    }
                                }
                            } else if (parsed.event === 'message_end') {
                                receivedMessageEnd = true;
                                finalizeBotMessage();

                                // Auto-generate title after first message exchange
                                if (currentConversation && currentConversation.messages.length <= 1) {
                                    const title = await generateTitle(
                                        difyConversationId || currentConversation.externalId || '',
                                        messages
                                    );

                                    if (title) {
                                        setState((prev) => ({
                                            ...prev,
                                            conversations: prev.conversations.map((conv) =>
                                                conv.id === targetConversationId
                                                    ? { ...conv, title }
                                                    : conv
                                            ),
                                        }));
                                    }
                                }
                            } else if (parsed.event === 'error') {
                                streamError = extractErrorMessage(parsed) || 'unknown error';
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }

            if (abortController.signal.aborted) {
                // Stopped by the user: stopStreaming already cleared the streaming flag
            } else if (streamError !== null) {
                failBotMessage(streamError);
            } else if (!receivedMessageEnd) {
                // Stream closed without Dify's message_end: keep what was
                // received, or report the coach as unavailable
                if (fullResponse) {
                    finalizeBotMessage();
                } else {
                    failBotMessage(undefined);
                }
            }
        } catch (error) {
            console.error('Error reading stream:', error);

            // Check if it was aborted
            const isAborted = error instanceof Error && error.name === 'AbortError';

            if (!isAborted) {
                // Connection dropped mid-answer: keep the partial answer, or
                // explain why the coach is unavailable
                if (fullResponse) {
                    finalizeBotMessage();
                } else {
                    failBotMessage(extractErrorMessage(error));
                }
            }
        } finally {
            // Clear abort controller and task ID when stream ends
            if (streamAbortControllerRef.current === abortController) {
                streamAbortControllerRef.current = null;
            }
            currentTaskIdRef.current = null;
        }
    }, [sendAPIMessage, generateTitle, formatUnavailableReason, t]);

    const stopStreaming = useCallback(async () => {
        // Call the stop API if we have a task ID
        if (currentTaskIdRef.current) {
            stopStreamingAPI(currentTaskIdRef.current);
            currentTaskIdRef.current = null;
        }

        if (streamAbortControllerRef.current) {
            streamAbortControllerRef.current.abort();
            streamAbortControllerRef.current = null;
            
            // Update message to remove isStreaming flag
            setState((prev) => ({
                ...prev,
                conversations: prev.conversations.map((conv) => ({
                    ...conv,
                    messages: conv.messages.map((msg) => 
                        msg.isStreaming ? { ...msg, isStreaming: false } : msg
                    ),
                })),
            }));
        }
    }, [stopStreamingAPI]);

    const value: IChatbotContext = {
        ...state,
        openChatbot,
        closeChatbot,
        toggleSidebar,
        createConversation,
        selectConversation,
        deleteConversation,
        archiveConversation,
        unarchiveConversation,
        renameConversation,
        sendMessage,
        stopStreaming,
    };

    return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
};
